import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, ShieldCheck, Leaf, BarChart3, Wrench, MapPinned } from '../components/Icons';

const stats = [
  { label: 'Owned operating plants', value: '2', icon: MapPinned },
  { label: 'Operational capacity', value: '5 MW', icon: Sun },
  { label: 'Hybrid-ready roadmap', value: 'Solar • Storage', icon: BarChart3 },
];

const pillars = [
  {
    title: 'Bankable execution',
    desc: 'Disciplined project development, clear timelines, and predictable execution form the foundation of our work.',
    icon: ShieldCheck,
  },
  {
    title: 'High-performance assets',
    desc: 'Our engineering and design decisions prioritize energy yield and operational simplicity, ensuring long-term performance for assets we own.',
    icon: Sun,
  },
  {
    title: 'Sustainability-first',
    desc: 'We focus on delivering cleaner energy today while contributing to stronger, more resilient communities over the long term. Every project is designed and built for durability.',
    icon: Leaf,
  },
  {
    title: 'O&M ready',
    desc: 'Through continuous monitoring, preventive maintenance, and timely intervention, we maintain reliable output year after year.',
    icon: Wrench,
  },
];

function GlowCard({ children }) {
  return (
    <div className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)] backdrop-blur">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-emerald-400/15 blur-3xl transition-opacity group-hover:opacity-100 opacity-70" />
      <div className="relative">{children}</div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="mx-auto max-w-6xl px-4">
      {/* Hero */}
      <section className="relative">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/70 animate-fadeInUp">
              <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
              Pune based renewable infrastructure owner & operator
            </p>

            <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl animate-fadeInUp [animation-delay:80ms]">
              Clean Power.
              <span className="block bg-gradient-to-r from-emerald-300 via-cyan-200 to-lime-200 bg-clip-text text-transparent">
                Clear Future.
              </span>
            </h1>

            <p className="mt-4 max-w-xl text-base leading-relaxed text-white/80 animate-fadeInUp [animation-delay:140ms]">
              Praash Green Energy develops, builds, owns, and operates renewable energy assets starting with
              high-performance solar parks and expanding into storage, wind, and hybrid systems.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row animate-fadeInUp [animation-delay:200ms]">
              <Link
                to="/solar-parks"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-400/90 px-5 py-3 text-sm font-semibold text-black shadow hover:bg-emerald-300"
              >
                View our projects <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                Partner with us <ArrowRight className="h-4 w-4 opacity-80" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <GlowCard>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Solar park snapshot</div>
                  <div className="mt-1 text-xs text-white/60">Real projects, real output — built for scale.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <Sun className="h-5 w-5 text-emerald-200" />
                </div>
              </div>

              <div className="mt-6 grid gap-3">
                {stats.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 p-4">
                      <div className="flex items-center gap-3">
                        <div className="rounded-xl bg-white/5 p-2">
                          <Icon className="h-4 w-4 text-white/80" />
                        </div>
                        <div className="text-sm text-white/70">{s.label}</div>
                      </div>
                      <div className="text-lg font-semibold">{s.value}</div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-2xl border border-white/10 bg-gradient-to-r from-emerald-400/10 via-cyan-300/10 to-lime-300/10 p-4">
                <div className="text-sm font-semibold">What we do</div>
                <div className="mt-2 text-sm text-white/70">
                  Development • Infrastructure • Ownership • Operations
                </div>
              </div>
            </GlowCard>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="mt-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-xs tracking-widest text-white/60">WHY PRAASH</div>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Built for performance & trust</h2>
          </div>
          <Link to="/about" className="hidden sm:inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm hover:bg-white/10">
            Learn more <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <GlowCard key={p.title}>
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <Icon className="h-5 w-5 text-emerald-200" />
                  </div>
                  <div>
                    <div className="text-base font-semibold">{p.title}</div>
                    <div className="mt-1 text-sm leading-relaxed text-white/70">{p.desc}</div>
                  </div>
                </div>
              </GlowCard>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-16">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-24 -bottom-24 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl" />

          <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <div className="text-xs tracking-widest text-white/60">NEXT STEP</div>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight">Let’s grow clean infrastructure</h3>
              <p className="mt-2 max-w-xl text-sm text-white/70">
                Reach out for partnerships, land opportunities, offtake discussions, or supply collaborations.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-black hover:bg-white/90"
            >
              Contact us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
